import { UserRole, OnboardingFlow, TrainingContent, GuidedTour } from '../types/onboarding';

export const USER_ROLES: UserRole[] = [
  {
    id: 'country_director',
    name: 'Country Director',
    description: 'Senior leadership overseeing humanitarian operations in-country',
    capabilities: ['strategic_decisions', 'resource_allocation', 'team_management', 'stakeholder_relations'],
    primaryWorkflows: ['crisis_overview', 'decision_making', 'resource_planning', 'coordination']
  },
  {
    id: 'field_coordinator',
    name: 'Field Coordinator',
    description: 'Operational lead managing field activities and response coordination',
    capabilities: ['field_operations', 'team_coordination', 'logistics', 'situation_reporting'],
    primaryWorkflows: ['field_response', 'team_coordination', 'situation_monitoring', 'resource_management']
  },
  {
    id: 'program_officer',
    name: 'Program Officer',
    description: 'Technical specialist managing specific humanitarian programs',
    capabilities: ['program_management', 'technical_expertise', 'monitoring_evaluation', 'partner_coordination'],
    primaryWorkflows: ['program_monitoring', 'data_analysis', 'reporting', 'partner_management']
  },
  {
    id: 'data_analyst',
    name: 'Data Analyst',
    description: 'Specialist in humanitarian data analysis and visualization',
    capabilities: ['data_analysis', 'visualization', 'trend_analysis', 'reporting'],
    primaryWorkflows: ['data_monitoring', 'analysis', 'reporting', 'dashboard_management']
  },
  {
    id: 'logistics_officer',
    name: 'Logistics Officer',
    description: 'Specialist in supply chain and resource logistics',
    capabilities: ['supply_chain', 'resource_tracking', 'distribution', 'inventory'],
    primaryWorkflows: ['supply_management', 'distribution_tracking', 'inventory_control', 'logistics_planning']
  },
  {
    id: 'communications_officer',
    name: 'Communications Officer',
    description: 'Specialist in external communications and stakeholder engagement',
    capabilities: ['communications', 'stakeholder_engagement', 'media_relations', 'advocacy'],
    primaryWorkflows: ['communications_planning', 'media_management', 'stakeholder_coordination', 'advocacy']
  }
];

export const ONBOARDING_FLOWS: OnboardingFlow[] = [
  {
    id: 'country_director_flow',
    name: 'Country Director Onboarding',
    description: 'Strategic overview and decision-making orientation for senior leadership',
    role: USER_ROLES[0],
    totalEstimatedMinutes: 45,
    steps: [
      {
        id: 'welcome',
        title: 'Welcome to CLEAR',
        description: 'Introduction to the Crisis Learning, Early-warning, Anticipation, and Response platform',
        component: 'WelcomeStep',
        required: true,
        estimatedMinutes: 5
      },
      {
        id: 'role_overview',
        title: 'Country Director Dashboard',
        description: 'Overview of strategic dashboards and decision support tools',
        component: 'RoleOverviewStep',
        required: true,
        estimatedMinutes: 10
      },
      {
        id: 'crisis_management',
        title: 'Crisis Management Workflow',
        description: 'Learn how to assess, prioritize, and respond to crisis situations',
        component: 'CrisisManagementStep',
        required: true,
        estimatedMinutes: 15
      },
      {
        id: 'decision_support',
        title: 'AI-Powered Decision Support',
        description: 'Using AI recommendations and evidence-based decision making',
        component: 'DecisionSupportStep',
        required: true,
        estimatedMinutes: 10
      },
      {
        id: 'team_coordination',
        title: 'Team Coordination Tools',
        description: 'Managing teams, assigning tasks, and monitoring progress',
        component: 'TeamCoordinationStep',
        required: false,
        estimatedMinutes: 5
      }
    ]
  },
  {
    id: 'field_coordinator_flow',
    name: 'Field Coordinator Onboarding',
    description: 'Operational training for field response coordination',
    role: USER_ROLES[1],
    totalEstimatedMinutes: 40,
    steps: [
      {
        id: 'welcome',
        title: 'Welcome to CLEAR',
        description: 'Introduction to field operations support',
        component: 'WelcomeStep',
        required: true,
        estimatedMinutes: 5
      },
      {
        id: 'field_operations',
        title: 'Field Operations Interface',
        description: 'Mobile-optimized tools for field coordination',
        component: 'FieldOperationsStep',
        required: true,
        estimatedMinutes: 15
      },
      {
        id: 'situation_reporting',
        title: 'Situation Reporting',
        description: 'Real-time situation updates and reporting workflows',
        component: 'SituationReportingStep',
        required: true,
        estimatedMinutes: 10
      },
      {
        id: 'resource_management',
        title: 'Resource Tracking',
        description: 'Managing supplies, personnel, and logistics in the field',
        component: 'ResourceManagementStep',
        required: true,
        estimatedMinutes: 10
      }
    ]
  },
  {
    id: 'data_analyst_flow',
    name: 'Data Analyst Onboarding',
    description: 'Technical training for data analysis and visualization',
    role: USER_ROLES[3],
    totalEstimatedMinutes: 35,
    steps: [
      {
        id: 'welcome',
        title: 'Welcome to CLEAR',
        description: 'Introduction to humanitarian data analysis platform',
        component: 'WelcomeStep',
        required: true,
        estimatedMinutes: 5
      },
      {
        id: 'data_sources',
        title: 'Data Sources & Integration',
        description: 'Understanding available data feeds and integration points',
        component: 'DataSourcesStep',
        required: true,
        estimatedMinutes: 10
      },
      {
        id: 'analysis_tools',
        title: 'Analysis Tools',
        description: 'Using built-in analysis tools and creating custom visualizations',
        component: 'AnalysisToolsStep',
        required: true,
        estimatedMinutes: 15
      },
      {
        id: 'dashboard_creation',
        title: 'Dashboard Creation',
        description: 'Building and sharing custom dashboards for stakeholders',
        component: 'DashboardCreationStep',
        required: false,
        estimatedMinutes: 5
      }
    ]
  }
];

export const TRAINING_CONTENT: TrainingContent[] = [
  {
    id: 'crisis_assessment_basics',
    title: 'Crisis Assessment Fundamentals',
    type: 'interactive',
    content: 'Interactive guide to humanitarian crisis assessment methodologies',
    duration: 20,
    role: [USER_ROLES[0], USER_ROLES[1], USER_ROLES[2]],
    difficulty: 'beginner',
    tags: ['crisis_assessment', 'methodology', 'basics'],
    lastUpdated: new Date('2024-01-15')
  },
  {
    id: 'data_visualization_principles',
    title: 'Humanitarian Data Visualization',
    type: 'video',
    content: 'Best practices for visualizing humanitarian data for decision makers',
    duration: 25,
    role: [USER_ROLES[3], USER_ROLES[2]],
    difficulty: 'intermediate',
    tags: ['data_visualization', 'dashboard_design', 'communication'],
    lastUpdated: new Date('2024-02-01')
  },
  {
    id: 'field_safety_protocols',
    title: 'Field Safety & Security Protocols',
    type: 'checklist',
    content: 'Comprehensive safety checklist for field operations',
    duration: 15,
    role: [USER_ROLES[1], USER_ROLES[4]],
    difficulty: 'beginner',
    tags: ['safety', 'security', 'field_operations'],
    lastUpdated: new Date('2024-01-30')
  },
  {
    id: 'ai_decision_support',
    title: 'AI-Powered Decision Making',
    type: 'simulation',
    content: 'Interactive simulation of AI-assisted humanitarian decision making',
    duration: 30,
    role: [USER_ROLES[0], USER_ROLES[2]],
    difficulty: 'advanced',
    tags: ['ai', 'decision_support', 'simulation'],
    lastUpdated: new Date('2024-02-10')
  }
];

export const GUIDED_TOURS: GuidedTour[] = [
  {
    id: 'dashboard_tour',
    name: 'Dashboard Overview',
    description: 'Quick tour of the main dashboard features',
    role: [USER_ROLES[0], USER_ROLES[1], USER_ROLES[2]],
    canSkip: true,
    autoStart: true,
    triggers: ['first_login', 'dashboard_visit'],
    steps: [
      {
        id: 'step1',
        element: '[data-tour="crisis-overview"]',
        title: 'Crisis Overview',
        content: 'This panel shows current active crises and their severity levels',
        placement: 'bottom',
        highlightElement: true,
        showBackButton: false,
        showNextButton: true
      },
      {
        id: 'step2',
        element: '[data-tour="ai-recommendations"]',
        title: 'AI Recommendations',
        content: 'AI-powered recommendations based on current situation analysis',
        placement: 'left',
        highlightElement: true,
        showBackButton: true,
        showNextButton: true
      },
      {
        id: 'step3',
        element: '[data-tour="action-center"]',
        title: 'Action Center',
        content: 'Quick access to common actions and workflows',
        placement: 'top',
        highlightElement: true,
        showBackButton: true,
        showNextButton: false,
        customButtons: [
          {
            text: 'Got it!',
            action: 'complete',
            style: 'primary'
          }
        ]
      }
    ]
  },
  {
    id: 'field_response_tour',
    name: 'Field Response Tools',
    description: 'Introduction to field coordination features',
    role: [USER_ROLES[1]],
    canSkip: true,
    autoStart: false,
    triggers: ['field_page_visit'],
    steps: [
      {
        id: 'step1',
        element: '[data-tour="mobile-interface"]',
        title: 'Mobile-Optimized Interface',
        content: 'All field tools are optimized for mobile and offline use',
        placement: 'center',
        highlightElement: false
      },
      {
        id: 'step2',
        element: '[data-tour="situation-updates"]',
        title: 'Real-time Updates',
        content: 'Send situation updates directly from the field',
        placement: 'bottom',
        highlightElement: true
      },
      {
        id: 'step3',
        element: '[data-tour="resource-tracking"]',
        title: 'Resource Tracking',
        content: 'Track supplies, personnel, and equipment in real-time',
        placement: 'top',
        highlightElement: true
      }
    ]
  }
];